<template>
  <div class="admin-login">
    <div class="admin-login__card">
      <div class="admin-login__brand">
        <div class="brand-mark">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" />
          </svg>
        </div>
        <h1 class="admin-login__title">Admin Sign In</h1>
        <p class="admin-login__subtitle">Sign in to access the management dashboard.</p>
      </div>

      <form class="admin-login__form" @submit.prevent="handleLogin">
        <div v-if="topError" class="admin-login__error">
          <i class="fa fa-triangle-exclamation"></i>
          {{ topError }}
        </div>

        <div class="rform__field">
          <label class="rform__label rform__label--required">Email</label>
          <input
            v-model="form.email"
            type="email"
            class="rform__input"
            :class="{ 'is-invalid': fieldError('email') }"
            placeholder="you@company.com"
            autocomplete="username"
            ref="email"
          />
          <span v-if="fieldError('email')" class="rform__error">{{ fieldError('email') }}</span>
        </div>

        <div class="rform__field">
          <label class="rform__label rform__label--required">Password</label>
          <input
            v-model="form.password"
            type="password"
            class="rform__input"
            :class="{ 'is-invalid': fieldError('password') }"
            placeholder="Enter your password"
            autocomplete="current-password"
            ref="password"
          />
          <span v-if="fieldError('password')" class="rform__error">{{ fieldError('password') }}</span>
        </div>

        <button type="submit" class="admin-login__submit" :disabled="submitting">
          <span v-if="submitting" class="admin-login__spinner"></span>
          {{ submitting ? 'Signing in…' : 'Sign In' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'

export default {
  name: 'AdminLogin',

  data() {
    return {
      form: {
        email: '',
        password: '',
      },
      errors: {},
      topError: '',
      submitting: false,
    }
  },

  methods: {
    fieldError(key) {
      const value = this.errors ? this.errors[key] : null
      if (!value) return ''
      return Array.isArray(value) ? value[0] : value
    },

    async handleLogin() {
      this.errors = {}
      this.topError = ''

      if (!this.form.email || !this.form.password) {
        if (!this.form.email) this.errors.email = 'Email is required.'
        if (!this.form.password) this.errors.password = 'Password is required.'
        return
      }

      this.submitting = true

      const result = await form_api_submit({
        url: 'auth/login',
        method: 'POST',
        data: {
          email: this.form.email,
          password: this.form.password,
        },
        show_success_toast: true,
        show_error_toast: false,
      })

      this.submitting = false

      if (result?.success) {
        const { token, user, company, role_code, companies_count } = result.data || {}

        if (token) localStorage.setItem('admin_token', token)
        if (user) {
          localStorage.setItem('admin_id', user.id)
          localStorage.setItem('admin_external_id', user.external_id || '')
          localStorage.setItem('admin_first_name', user.first_name || '')
          localStorage.setItem('admin_last_name', user.last_name || '')
          localStorage.setItem('admin_email', user.email || '')
        }
        if (company) {
          localStorage.setItem('admin_company_id', company.id)
          localStorage.setItem('admin_company_name', company.name || '')
        }
        if (role_code) localStorage.setItem('admin_role_code', role_code)
        if (companies_count > 1) {
          this.$router.push({ name: 'SelectCompany' })
        } else {
          this.$router.push({ name: 'Dashboard' })
        }
      } else if (result?.data?.error_type === 'invalid_credentials') {
        this.topError = 'Invalid email or password.'
      } else if (result?.data?.errors) {
        this.errors = result.data.errors
      } else {
        this.topError = result?.message || 'Could not sign in. Please try again.'
      }
    },
  },
}
</script>

<style scoped>
.admin-login {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, var(--color-tint-light) 0%, var(--color-surface) 60%);
  padding: 24px;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

.admin-login__card {
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.65);
  border-radius: 22px;
  padding: 36px 32px;
  box-shadow: 0 20px 50px -20px rgba(12, 18, 83, 0.3);
}

.admin-login__brand {
  text-align: center;
  margin-bottom: 28px;
}

.brand-mark {
  width: 48px;
  height: 48px;
  margin: 0 auto 14px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  box-shadow: 0 8px 20px -8px rgba(93, 28, 106, 0.5);
}
.brand-mark svg {
  width: 24px;
  height: 24px;
}

.admin-login__title {
  margin: 0 0 4px;
  font-size: 21px;
  font-weight: 800;
  color: var(--color-text-dark);
}
.admin-login__subtitle {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-muted);
}

.admin-login__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.admin-login__error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(179, 38, 30, 0.07);
  border: 1px solid rgba(179, 38, 30, 0.22);
  font-size: 13px;
  color: #b3261e;
  font-weight: 600;
}

.admin-login__submit {
  height: 46px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 10px 26px -10px rgba(93, 28, 106, 0.55);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
  margin-top: 4px;
}
.admin-login__submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 14px 32px -10px rgba(93, 28, 106, 0.65);
}
.admin-login__submit:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.admin-login__spinner {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  animation: admin-login-spin 0.7s linear infinite;
}
@keyframes admin-login-spin {
  to { transform: rotate(360deg); }
}
</style>